import { PageSize } from '@/constant';
import {
	GetWorkerUseTipListParams,
	ISystemUseTip,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import { formatSystemUseTipList } from './system-use-tip.helper';
import {
	ICreateSystemUseTipRequest,
	IPayloadDeleteSystemUseTipRequest,
	IPayloadGetSystemUseTipDetailRequest,
	IPayloadGetSystemUseTipDetailSuccess,
	IPayloadGetSystemUseTipListRequest,
	IPayloadGetSystemUseTipListSuccess,
	ISystemUseTipDetailStore,
} from './system-use-tip.type';

const InitParams: GetWorkerUseTipListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	list: [] as ISystemUseTipDetailStore[],
	total: 0,
	params: InitParams,
	detail: {} as ISystemUseTipDetailStore,
};

const systemUseTipSlice = createSlice({
	initialState,
	name: 'systemUseTip',
	reducers: {
		/**---- GET SYSTEM USE TIP LIST START ---- */
		getSystemUseTipListRequest: (
			state,
			action: PayloadAction<IPayloadGetSystemUseTipListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getSystemUseTipListSuccess: (
			state,
			action: PayloadAction<IPayloadGetSystemUseTipListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.list = content;
			state.total = total;
		},
		getSystemUseTipListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.list = [];
		},
		/**---- GET SYSTEM USE TIP LIST END ---- */

		/**---- CREATE SYSTEM USE TIP START ---- */
		createSystemUseTipRequest: (
			state,
			action: PayloadAction<ICreateSystemUseTipRequest>
		) => {
			state.fetching = true;
		},
		createSystemUseTipSuccess: (state) => {
			state.fetching = false;
		},
		createSystemUseTipFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE SYSTEM USE TIP END ---- */

		/**---- GET SYSTEM USE TIP DETAIL START ---- */
		getSystemUseTipDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetSystemUseTipDetailRequest>
		) => {
			state.fetching = true;
		},
		getSystemUseTipDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetSystemUseTipDetailSuccess>
		) => {
			const { detail } = action.payload;
			state.fetching = false;
			state.detail = detail;
		},
		getSystemUseTipDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET SYSTEM USE TIP DETAIL END ---- */

		/**---- DELETE SYSTEM USE TIP START ---- */
		deleteSystemUseTipRequest: (
			state,
			action: PayloadAction<IPayloadDeleteSystemUseTipRequest>
		) => {
			state.fetching = true;
		},
		deleteSystemUseTipSuccess: (state) => {
			state.fetching = false;
		},
		deleteSystemUseTipFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE SYSTEM USE TIP START ---- */

		/**---- UPDATE SYSTEM USE TIP START ---- */
		updateSystemUseTipSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ISystemUseTip>>
		) => {
			const { data } = action.payload;
			const list = [...state.list];
			state.list = formatSystemUseTipList(mergeListItemUpdated(list, data));
		},
		updateSystemUseTipDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ISystemUseTip>>
		) => {
			const { data } = action.payload;
			const detail = { ...state.detail };
			state.detail = mergeItemUpdated<ISystemUseTipDetailStore, ISystemUseTip>(
				detail,
				data
			);
		},
		/**---- UPDATE COMMON NOTICE END ---- */
	},
});

export const SystemUseTipSelectors = {
	selectFetching: (state: RootState) => state.systemUseTip.fetching,
	selectParams: (state: RootState) => state.systemUseTip.params,
	selectList: (state: RootState) => state.systemUseTip.list,
	selectTotal: (state: RootState) => state.systemUseTip.total,
	selectDetail: (state: RootState) => state.systemUseTip.detail,
};

export const SystemUseTipActions = systemUseTipSlice.actions;
export const SystemUseTipReducer = systemUseTipSlice.reducer;
